import {
  Box,
  Button,
  Flex,
  FormInput,
  Heading,
  IconButton,
  Input,
  Text,
  toast,
} from '@sparrowengg/twigs-react';
import { PlusIcon, DeleteIcon } from '@sparrowengg/twigs-react-icons';
import FileDownload from 'commons/icons/file-download';
import { IRootDispatch, IRootState } from 'commons/types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateSettings } from 'redux-store/actions';
import { zipSSDKFile } from '../services';
import { dependenciesType, whiteListedDomainsType } from '../types';

const Settings = () => {
  const [whitelistedDomainsState, setWhitelistedDomainsState] = useState<
    whiteListedDomainsType[]
  >([]);
  const [dependenciesState, setDependenciesState] = useState<
    dependenciesType[]
  >([]);
  const [zipFileLoader, setZipFileLoader] = useState(false);
  const dispatch: IRootDispatch = useDispatch();
  const currentApp = useSelector((state: IRootState) => state.currentApp);
  const isValidated = useSelector((state: IRootState) => state.appValidation.isValidated);

  const { whitelistedDomains, dependencies } = useSelector(
    (state: IRootState) => state?.settings
  );


  const saveSettings = ( domains: whiteListedDomainsType[], deps: dependenciesType[]) => {
    // Update Redux store directly
    dispatch(updateSettings({
        whitelistedDomains: domains,
        dependencies: deps
    }));
};


  const downloadZipFile = async () => {
    setZipFileLoader(true);
    try {
      const response = await zipSSDKFile(`${currentApp.appVersionId}`);

      // Create a Blob from the binary data
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'download.zip';

      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
      toast({
        variant: "error",
        description: "Error when downloading the zip file"
        })
    } finally {
      setZipFileLoader(false);
    }
  };

  useEffect(() => {
    setWhitelistedDomainsState(whitelistedDomains);
    setDependenciesState(dependencies);
  }, [whitelistedDomains, dependencies]);

  // Update domains with debounced save
  const handleWhitelistedDomainsChange = (updatedDomains: whiteListedDomainsType[]) => {
    setWhitelistedDomainsState(updatedDomains);
    saveSettings(updatedDomains, dependenciesState);
  };

  // Update dependencies with debounced save
  const handleDependenciesChange = (updatedDependencies: dependenciesType[]) => {
    setDependenciesState(updatedDependencies);
    saveSettings(whitelistedDomainsState, updatedDependencies);
  };

  return (
    <Box
      css={{
        height: 'calc(100vh - 73px)',
        width: '100%',
        overflow: 'auto',
        background: '#151515',
        input: {
          background: '$white200 !important',
          color: '$white900',
          border: '$black300 !important',
          boxShadow: 'none !important',
          '&:focus, &:hover, &:active, "&:-internal-autofill-selected"': {
            background: '$white200 !important'
          }
        }
      }}
    >
      <Flex alignItems="center" justifyContent="center">
        <Box css={{ width: 800, marginTop: '$36' }}>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            css={{ position: 'relative' }}
          >
            <Flex flexDirection="column" gap="$2">
              <Flex alignItems="center" gap="$2">
                <Heading size="h4" css={{ color: '$white900' }}>
                  Settings
                </Heading>
              </Flex>
              <Text size="sm" css={{ color: '$white600' }}>
                Configure the app settings
              </Text>
            </Flex>
          </Flex>
          <Flex flexDirection="column" gap="$10" css={{ marginTop: '$28' }}>
            <Flex flexDirection="column" gap="$2">
              <Text size="md" weight="bold">
                Whitelisted Domains
              </Text>
              <Text size="sm" css={{ color: '$white600' }}>
                Specify all the domains to be accessed from the app
              </Text>
            </Flex>
            {whitelistedDomainsState.length > 0 && (
              <Flex flexDirection="column" gap="$6">
                {whitelistedDomainsState.map((domain) => (
                  <Flex
                    key={domain.id}
                    alignItems="center"
                    gap="$4"
                    css={{
                      '&:hover #delete-btn': {
                        opacity: 1
                      }
                    }}
                  >
                    <Input
                      size="lg"
                      value={domain.domain}
                      key={domain.id}
                      css={{
                        width: 376
                      }}
                      onChange={(e) => {
                        const currentDomainValue = e?.currentTarget?.value;
                        const updatedDomains = whitelistedDomainsState.map((currentDomain) =>
                          currentDomain.id === domain.id
                            ? {
                                ...currentDomain,
                                domain: currentDomainValue
                              }
                            : currentDomain
                        );
                        handleWhitelistedDomainsChange(updatedDomains);
                      }}
                    />
                    <IconButton
                      icon={<DeleteIcon size={24} />}
                      variant="ghost"
                      size="md"
                      color="default"
                      id="delete-btn"
                      css={{ opacity: 0 }}
                      onClick={() => {
                        const updatedDomains = whitelistedDomainsState.filter(
                          (currentDomain) => currentDomain.id !== domain.id
                        );
                        handleWhitelistedDomainsChange(updatedDomains);
                      }}
                    />
                  </Flex>
                ))}
              </Flex>
            )}
            <Button
              css={{ width: 'max-content' }}
              size="md"
              variant="ghost"
              leftIcon={<PlusIcon />}
              onClick={() => {
                const updatedDomains = [
                  ...whitelistedDomainsState,
                  { id: whitelistedDomainsState.length + 1, domain: '' }
                ];
                handleWhitelistedDomainsChange(updatedDomains);
              }}
            >
              Add New Domain
            </Button>
          </Flex>
          <Flex flexDirection="column" gap="$10" css={{ marginTop: '$20' }}>
            <Flex alignItems="center" justifyContent="space-between">
              <Flex flexDirection="column" gap="$2">
                <Flex alignItems="center" gap="$2">
                  <Text size="md" weight="bold" css={{ color: '$white900' }}>
                    Dependencies
                  </Text>
                </Flex>
                <Text size="sm" css={{ color: '$white600' }}>
                  Specify all the dependencies to be used within the app
                </Text>
              </Flex>
            </Flex>
            {dependenciesState.length > 0 && (
              <Flex flexDirection="column" gap="$6">
                {dependenciesState.map((dependency) => (
                  <Flex key={dependency.id} alignItems="flex-end" gap="$6">
                    <Box css={{ flex: 1 }}>
                      <FormInput
                        label="NPM Package"
                        size="lg"
                        value={dependency.dependency}
                        onChange={(e) => {
                          const currentDependencyValue = e?.currentTarget?.value;
                          const updatedDependencies = dependenciesState.map((currentDependency) =>
                            currentDependency.id === dependency.id
                              ? {
                                  ...currentDependency,
                                  dependency: currentDependencyValue
                                }
                              : currentDependency
                          );
                          handleDependenciesChange(updatedDependencies);
                        }}
                      />
                    </Box>
                    <Box css={{ flex: 1 }}>
                      <FormInput
                        size="lg"
                        label="NPM Package Version"
                        value={dependency.version}
                        onChange={(e) => {
                          const currentDependencyValue = e?.currentTarget?.value;
                          const updatedDependencies = dependenciesState.map((currentDependency) =>
                            currentDependency.id === dependency.id
                              ? {
                                  ...currentDependency,
                                  version: currentDependencyValue
                                }
                              : currentDependency
                          );
                          handleDependenciesChange(updatedDependencies);
                        }}
                      />
                    </Box>
                    <IconButton
                      icon={<DeleteIcon size={24} />}
                      variant="ghost"
                      size="md"
                      color="default"
                      onClick={() => {
                        const updatedDependencies = dependenciesState.filter(
                          (currentDependency) =>
                            currentDependency.id !== dependency.id
                        );
                        handleDependenciesChange(updatedDependencies);
                      }}
                    />
                  </Flex>
                ))}
              </Flex>
            )}
            <Button
              size="md"
              css={{ width: 'max-content' }}
              variant="ghost"
              leftIcon={<PlusIcon />}
              onClick={() => {
                const updatedDependencies = [
                  ...dependenciesState,
                  { id: dependenciesState.length + 1, dependency: '', version: '' }
                ];
                handleDependenciesChange(updatedDependencies);
              }}
            >
              Add New Dependency
            </Button>
          </Flex>
          <Flex flexDirection="column" gap="$10" css={{ marginTop: '$20' }}>
            <Flex alignItems="center" justifyContent="space-between">
              <Flex flexDirection="column" gap="$2">
                <Flex alignItems="center" gap="$2">
                  <Text size="md" weight="bold" css={{ color: '$white900' }}>
                    Export App Package (.zip file)
                  </Text>
                </Flex>
                <Text size="sm" css={{ color: '$white600' }}>
                  Click on the below button to obtain the app package as a .zip
                  file
                </Text>
              </Flex>
            </Flex>
            <Button
              size="md"
              css={{ width: 'max-content' }}
              variant="ghost"
              disabled={!isValidated || zipFileLoader}
              onClick={() => downloadZipFile()}
              leftIcon={<FileDownload />}
              loading={zipFileLoader}
            >
              Export App Package
            </Button>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

export default Settings;